import { buildAlbumHtml, type HtmlItem } from './exportHtml';
import { sanitizeAlbumName, thumbName } from './exportNaming';
import { ExportFatalError } from './types';
import type { ExportItem, ExportSink, FetchFn, FsDirHandle } from './types';

const FATAL_WRITE_ERRORS = new Set(['QuotaExceededError', 'NotAllowedError', 'NoModificationAllowedError']);

function asFatal(err: unknown): unknown {
  if (err instanceof Error && FATAL_WRITE_ERRORS.has(err.name)) {
    return new ExportFatalError('The folder is full or can no longer be written to. Files saved so far were kept.');
  }
  return err;
}

/** Size of a file in a directory, or -1 when it is not there. */
async function sizeOf(dir: FsDirHandle, name: string): Promise<number> {
  try {
    return (await (await dir.getFileHandle(name)).getFile()).size;
  } catch {
    return -1;
  }
}

/** Streams a response body to a file chunk by chunk, so nothing is buffered in memory. */
async function streamToFile(body: ReadableStream<Uint8Array>, dir: FsDirHandle, name: string): Promise<void> {
  const writable = await (await dir.getFileHandle(name, { create: true })).createWritable();
  const reader = body.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      await writable.write(value);
    }
    await writable.close();
  } catch (err) {
    await reader.cancel().catch(() => undefined); // stop the download
    await writable.abort().catch(() => undefined);
    // Creating the handle left an empty file; a complete file from an earlier export is kept.
    if ((await sizeOf(dir, name)) === 0) await dir.removeEntry(name).catch(() => undefined);
    throw asFatal(err);
  }
}

async function writeWhole(dir: FsDirHandle, name: string, data: Blob | string): Promise<void> {
  const writable = await (await dir.getFileHandle(name, { create: true })).createWritable();
  await writable.write(data);
  await writable.close();
}

/** Sink that writes `<root>/<album>/media`, `thumbs` and `index.html`. */
export async function createFolderSink(
  root: FsDirHandle,
  title: string,
  allItems: ExportItem[],
  fetchFn: FetchFn = fetch,
): Promise<ExportSink> {
  const album = await root.getDirectoryHandle(sanitizeAlbumName(title), { create: true });
  const media = await album.getDirectoryHandle('media', { create: true });
  const thumbs = await album.getDirectoryHandle('thumbs', { create: true });

  return {
    async has(item) {
      return item.size !== null && (await sizeOf(media, item.filename)) === item.size;
    },

    async write(item, signal) {
      const res = await fetchFn(item.url, { signal });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      await streamToFile(res.body, media, item.filename);
      if (!item.thumbnailUrl) return;
      try {
        const thumb = await fetchFn(item.thumbnailUrl, { signal });
        if (thumb.ok) await writeWhole(thumbs, thumbName(item.filename), await thumb.blob());
      } catch {
        // A missing preview only costs a placeholder tile.
      }
    },

    async finish() {
      const present: HtmlItem[] = [];
      for (const item of allItems) {
        if ((await sizeOf(media, item.filename)) <= 0) continue;
        const hasThumb = (await sizeOf(thumbs, thumbName(item.filename))) > 0;
        present.push({ filename: item.filename, mediaType: item.mediaType, hasThumb });
      }
      const html = buildAlbumHtml({ title, items: present, partLabel: null, exportedAt: new Date() });
      try {
        await writeWhole(album, 'index.html', html);
      } catch (err) {
        throw asFatal(err);
      }
    },
  };
}
